import Copyright from '../components/Copyright';
import React, { useState } from 'react';
import { userAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: user?.email || '',
    height: user?.height || '',
    weight: user?.weight || '',
    gender: user?.gender || '',
    measurement_scale: user?.measurement_scale || 'imperial',
  });
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const setPw = k => e => setPwForm(p => ({ ...p, [k]: e.target.value }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await userAPI.updateProfile({
        email: form.email,
        height: form.height ? parseFloat(form.height) : undefined,
        weight: form.weight ? parseFloat(form.weight) : undefined,
        gender: form.gender || undefined,
        measurement_scale: form.measurement_scale,
      });
      updateUser(res.data);
      toast.success('Profile updated!');
    } catch (err) { toast.error(err.response?.data?.error || 'Update failed'); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwForm.newPassword !== pwForm.confirm) { toast.error("Passwords don't match"); return; }
    if (pwForm.newPassword.length < 8) { toast.error('Min 8 characters'); return; }
    setPwSaving(true);
    try {
      await userAPI.changePassword(pwForm.currentPassword, pwForm.newPassword);
      toast.success('Password changed!');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setPwSaving(false); }
  };

  const exportData = async () => {
    try {
      const res = await userAPI.exportData();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MyIronJournal-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded!');
    } catch { toast.error('Export failed'); }
  };

  const deleteAccount = async () => {
    if (!deletePassword) { toast.error('Password required'); return; }
    setDeleting(true);
    try {
      await userAPI.deleteAccount(deletePassword);
      logout();
      navigate('/login');
      toast.success('Account deleted');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">PROFILE</h1>
        <button className="btn btn-secondary" onClick={() => { logout(); navigate('/login'); }}>
          🚪 Sign Out
        </button>
      </div>

      <div className="page-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* Left */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Account Info */}
          <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 60, height: 60, background: 'var(--red)', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontSize: 28, color: 'white', flexShrink: 0 }}>
              {user?.username?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 20 }}>{user?.username}</div>
              <div style={{ color: 'var(--text-2)', fontSize: 14 }}>{user?.email}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </div>
            </div>
          </div>

          {/* Edit Profile */}
          <div className="card">
            <div className="section-title">👤 Edit Profile</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="input-group">
                <label className="input-label">Email</label>
                <input className="input" type="email" value={form.email} onChange={set('email')} />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {['imperial', 'metric'].map(s => (
                  <button key={s} type="button"
                    onClick={() => setForm(p => ({ ...p, measurement_scale: s }))}
                    className={`btn btn-sm ${form.measurement_scale === s ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ flex: 1 }}>
                    {s === 'imperial' ? 'Imperial (lbs/in)' : 'Metric (kg/cm)'}
                  </button>
                ))}
              </div>

              <div className="input-row">
                <div className="input-group">
                  <label className="input-label">{form.measurement_scale === 'imperial' ? 'Height (in)' : 'Height (cm)'}</label>
                  <input className="input" type="number" value={form.height} onChange={set('height')} />
                </div>
                <div className="input-group">
                  <label className="input-label">{form.measurement_scale === 'imperial' ? 'Weight (lbs)' : 'Weight (kg)'}</label>
                  <input className="input" type="number" value={form.weight} onChange={set('weight')} />
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">Gender</label>
                <select className="input" value={form.gender} onChange={set('gender')}>
                  <option value="">Select</option>
                  {['Male','Female','Non-binary','Prefer not to say'].map(g => <option key={g}>{g}</option>)}
                </select>
              </div>

              <button className="btn btn-primary" onClick={saveProfile} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        {/* Right */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Change Password */}
          <div className="card">
            <div className="section-title">🔒 Change Password</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[['currentPassword','Current Password'],['newPassword','New Password'],['confirm','Confirm Password']].map(([k,l]) => (
                <div key={k} className="input-group">
                  <label className="input-label">{l}</label>
                  <input className="input" type="password" value={pwForm[k]} onChange={setPw(k)} />
                </div>
              ))}
              <button className="btn btn-secondary" onClick={changePassword} disabled={pwSaving}>
                {pwSaving ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>

          {/* Data & Privacy */}
          <div className="card">
            <div className="section-title">📊 Data & Privacy</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 8 }}>
                  Download all your health metrics as a CSV file.
                </p>
                <button className="btn btn-secondary btn-full" onClick={exportData}>
                  📥 Export My Data (CSV)
                </button>
              </div>
              <div className="divider" />
              <div>
                <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 8 }}>
                  Read our <a href="https://myironjournal.com/privacypolicy.html" target="_blank" rel="noreferrer">Privacy Policy</a>
                </p>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="card" style={{ borderColor: 'var(--red-dim)', background: 'rgba(233,69,96,0.05)' }}>
            <div className="section-title" style={{ color: 'var(--red)' }}>⚠️ Danger Zone</div>
            {!showDelete ? (
              <>
                <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 12 }}>
                  Permanently delete your account and all data. This cannot be undone.
                </p>
                <button className="btn btn-danger btn-full" onClick={() => setShowDelete(true)}>
                  🗑️ Delete My Account & Data
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ fontSize: 14, color: 'var(--red)', fontWeight: 600 }}>
                  This will permanently delete everything. Enter your password to confirm.
                </p>
                <input className="input" type="password" value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)} placeholder="Your password" />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-danger" onClick={deleteAccount} disabled={deleting || !deletePassword}>
                    {deleting ? 'Deleting...' : 'Delete Everything'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => { setShowDelete(false); setDeletePassword(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    <Copyright />
    </div>
  );
}
